import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, ChalkboardTeacher, Certificate, Clock, CurrencyDollar,
  Star, CalendarBlank, Envelope, Phone, MapPin, Briefcase, PencilLine,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { TeacherForm } from "./Teachers";

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
    </div>
  );
}

export default function TeacherProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t: tt } = useTranslation();
  const [t, setT] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const canManage = ["super_admin", "director", "branch_manager"].includes(user?.role);

  const load = () => api.get(`/teachers/${id}`).then(({ data }) => setT(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [id]);

  if (!t) return <div className="text-sm text-muted-foreground">Loading profile...</div>;

  const avgRating = t.reviews?.length
    ? (t.reviews.reduce((s, r) => s + r.rating, 0) / t.reviews.length).toFixed(1)
    : "—";

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => nav("/teachers")} className="gap-2 -ml-3">
        <ArrowLeft size={16} /> Back to Teachers
      </Button>

      {canManage && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Manrope" }}>{tt("teachers.editTeacher", { defaultValue: "Edit Teacher" })}</DialogTitle>
            </DialogHeader>
            <TeacherForm initial={t} onSave={() => load()} onClose={() => setEditOpen(false)} />
          </DialogContent>
        </Dialog>
      )}

      <Card className="border-border/60 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-primary via-primary to-destructive/60" />
        <CardContent className="p-6 -mt-12">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="w-24 h-24 rounded-xl bg-card border-4 border-card shadow-md overflow-hidden flex items-center justify-center text-3xl font-bold">
              {t.full_name?.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t.subject}</div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "Manrope" }}>{t.full_name}</h1>
              <div className="flex flex-wrap gap-2 mt-2">
                {(t.languages || []).map((l) => <Badge key={l} variant="secondary">{l}</Badge>)}
                <Badge className={t.status === "active" ? "bg-emerald-500 text-white" : "bg-muted text-foreground"}>{t.status}</Badge>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="text-center px-4">
                <div className="text-2xl font-bold font-mono">{t.classes?.length || 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Classes</div>
              </div>
              <div className="text-center px-4 border-l border-border">
                <div className="text-2xl font-bold font-mono flex items-center gap-1 justify-center">
                  {avgRating}<Star size={14} weight="fill" className="text-amber-500" />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Rating</div>
              </div>
              <div className="text-center px-4 border-l border-border">
                <div className="text-2xl font-bold font-mono">${t.monthly_salary || 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Salary/mo</div>
              </div>
              {canManage && (
                <Button
                  data-testid="edit-teacher-btn" variant="outline" size="sm"
                  onClick={() => setEditOpen(true)} className="gap-1.5 self-center rounded-full"
                >
                  <PencilLine size={14} weight="bold" /> {tt("common.edit", { defaultValue: "Edit" })}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-t-overview">Overview</TabsTrigger>
          <TabsTrigger value="qualifications" data-testid="tab-t-qualifications">Qualifications</TabsTrigger>
          <TabsTrigger value="classes" data-testid="tab-t-classes">Classes</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-t-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-t-hours">Working Hours</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-t-payroll">Payroll</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-t-leaves">Leave Requests</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-t-performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Card className="border-border/60 md:col-span-2"><CardContent className="p-5">
            <h3 className="font-semibold mb-3" style={{ fontFamily: "Manrope" }}>About</h3>
            <p className="text-sm text-muted-foreground">{t.bio || "No bio available."}</p>
          </CardContent></Card>
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-3" style={{ fontFamily: "Manrope" }}>Contact</h3>
            <Row label="Email" value={t.email} />
            <Row label="Phone" value={t.phone} />
            <Row label="Address" value={t.address} />
            <Row label="Nationality" value={t.nationality} />
            <Row label="Hire Date" value={t.hire_date} />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="qualifications" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Qualifications</h3>
            {(t.qualifications || []).length === 0 && <p className="text-sm text-muted-foreground">No qualifications on file.</p>}
            <ul className="space-y-3">
              {(t.qualifications || []).map((q, i) => (
                <li key={i} className="flex items-start gap-3 pb-3 border-b border-border last:border-0">
                  <Certificate size={20} weight="duotone" className="text-primary mt-0.5" />
                  <div>
                    <div className="font-semibold text-sm">{q.degree}</div>
                    <div className="text-xs text-muted-foreground">{q.institution} · {q.year}</div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="classes" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Assigned Classes</h3>
            {(t.classes || []).length === 0 ? <p className="text-sm text-muted-foreground">No assigned classes.</p> :
              <ul className="divide-y divide-border">
                {t.classes.map((c) => (
                  <li key={c.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.schedule} · {c.room}</div>
                    </div>
                    <Badge variant="outline">{c.student_ids?.length || 0} students</Badge>
                  </li>
                ))}
              </ul>
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="schedule" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Upcoming Lessons</h3>
            {(t.upcoming_lessons || []).length === 0 ? <p className="text-sm text-muted-foreground">No upcoming lessons.</p> :
              <ul className="space-y-2">
                {t.upcoming_lessons.map((l) => (
                  <li key={l.id} className="flex items-center justify-between p-3 rounded-md border border-border/60">
                    <div>
                      <div className="font-semibold text-sm">{l.title}</div>
                      <div className="text-xs text-muted-foreground font-mono">{l.date} · {l.start_time} · {l.room}</div>
                    </div>
                    <Badge variant="outline">{l.status}</Badge>
                  </li>
                ))}
              </ul>
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="hours" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Working Hours</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {(t.working_hours || []).map((h, i) => (
                <div key={i} className="p-3 rounded-md border border-border/60 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{h.day}</div>
                  <div className="font-mono font-semibold text-sm mt-1">{h.from} – {h.to}</div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="payroll" className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Monthly Salary</div>
            <div className="text-3xl font-bold font-mono mt-1">${t.monthly_salary || 0}</div>
          </CardContent></Card>
          <Card className="border-border/60"><CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Hourly Rate</div>
            <div className="text-3xl font-bold font-mono mt-1">${t.hourly_rate || 0}</div>
          </CardContent></Card>
          <Card className="border-border/60"><CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Annual (est.)</div>
            <div className="text-3xl font-bold font-mono mt-1">${(t.monthly_salary || 0) * 12}</div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="leaves" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Leave Requests</h3>
            {(t.leaves || []).length === 0 ? <p className="text-sm text-muted-foreground">No leave requests.</p> :
              <table className="w-full text-sm">
                <thead className="border-b border-border"><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="py-2">Type</th><th>From</th><th>To</th><th>Reason</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {t.leaves.map((l) => (
                    <tr key={l.id} className="border-b border-border last:border-0">
                      <td className="py-2 capitalize text-xs">{l.type}</td>
                      <td className="font-mono text-xs">{l.start_date}</td>
                      <td className="font-mono text-xs">{l.end_date}</td>
                      <td className="text-xs">{l.reason}</td>
                      <td>
                        <Badge variant={l.status === "approved" ? "default" : l.status === "rejected" ? "destructive" : "secondary"} className="capitalize">{l.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="performance" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(t.reviews || []).map((r) => (
              <Card key={r.id} className="border-border/60"><CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{r.period}</span>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} weight="fill" className={cn(i < r.rating ? "text-amber-500" : "text-muted")} />
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Student Feedback</div>
                    <div className="font-mono font-bold">{r.student_feedback_score}/5</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Attendance</div>
                    <div className="font-mono font-bold">{r.attendance_score}%</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3 italic">"{r.comments}"</p>
                <div className="text-[10px] text-muted-foreground mt-2">— {r.reviewer}</div>
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
